import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, Building2, CheckCircle, XCircle, AlertCircle, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import Papa from 'papaparse';
import { CompanyInfo, VerificationType, BatchVerificationResult, DualBatchResult } from '../types';
import { VatVerificationService } from '../services/vatVerificationService';
import { PeppolVerificationService } from '../services/peppolVerificationService';
import { DualVerificationService } from '../services/dualVerificationService';
import { useLocalization } from '../contexts/LocalizationContext';
import { Analytics } from '../utils/analytics';
import ServiceUnavailableModal from './ServiceUnavailableModal';

const BatchVerification: React.FC = () => {
  const { t, language } = useLocalization();
  const [verificationType, setVerificationType] = useState<VerificationType>('vat');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<BatchVerificationResult | DualBatchResult | null>(null);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [useRealAPI, setUseRealAPI] = useState(true);
  const [inputMethod, setInputMethod] = useState<'csv' | 'textarea'>('textarea');
  const [textAreaInput, setTextAreaInput] = useState('');
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [serviceUnavailableModal, setServiceUnavailableModal] = useState<{
    isOpen: boolean;
    serviceName: string;
  }>({ isOpen: false, serviceName: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleVerificationTypeChange = (newType: VerificationType) => {
    setVerificationType(newType);
    setResults(null); // Clear previous validation results
    setProgress(null); // Clear any progress indicators
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Analytics.batchVerificationUpload(verificationType === 'both' ? 'dual' : verificationType, file.type, file.size);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setUploadedData(results.data);
        setResults(null);
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        alert('Error parsing CSV file. Please check the format.');
      }
    });
  };

  const parseTextAreaInput = () => {
    if (!textAreaInput.trim()) return [];

    const lines = textAreaInput.trim().split('\n');
    const data: any[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (trimmedLine) {
        // Split by comma and clean up each identifier
        const identifiers = trimmedLine.split(',').map(id => id.trim()).filter(id => id);
        
        identifiers.forEach(identifier => {
          data.push({
            identifier: identifier,
            name: `Company ${data.length + 1}`,
            // For backward compatibility, also set common column names
            vatNumber: identifier,
            participantId: identifier,
            id: identifier
          });
        });
      }
    });

    return data;
  };

  const processBatchVerification = async () => {
    // Get data from either CSV upload or text area input
    const dataToProcess = inputMethod === 'csv' ? uploadedData : parseTextAreaInput();
    
    if (dataToProcess.length === 0) {
      alert('Please upload a CSV file or enter identifiers in the text area.');
      return;
    }

    setIsProcessing(true);
    setProgress({ current: 0, total: dataToProcess.length });
    const startTime = Date.now();

    // Set the API mode for all services
    DualVerificationService.setUseRealAPI(useRealAPI);

    try {
      if (verificationType === 'vat') {
        const companies: CompanyInfo[] = dataToProcess.map((row, index) => ({
          name: '',
          vatNumber: row.vatNumber || row.vat || row.taxId || row.identifier || '',
          address: '',
          country: '',
          city: '',
          postalCode: ''
        }));

        // Initialize results with empty array
        const initialResult: BatchVerificationResult = {
          totalProcessed: 0,
          validCount: 0,
          invalidCount: 0,
          errorCount: 0,
          results: [],
          processingTime: 0
        };
        setResults(initialResult);

        // Process each company individually and update results in real-time
        for (let i = 0; i < companies.length; i++) {
          const result = await VatVerificationService.verifyVatNumber(companies[i], language);
          
          // Check if result contains service unavailable error
          if (result.error) {
            // Show service unavailable modal and stop processing
            setServiceUnavailableModal({
              isOpen: true,
              serviceName: 'VAT Verification'
            });
            break; // Stop processing remaining items
          }
        
          // Update progress
          setProgress({ current: i + 1, total: companies.length });
          
          // Update results with the new result
          setResults(prev => {
            if (!prev || !('results' in prev)) return initialResult;
            const newResults = [...prev.results, result];
            const validCount = newResults.filter(r => r.isValid).length;
            const invalidCount = newResults.filter(r => !r.isValid && !r.error).length;
            const errorCount = newResults.filter(r => r.error).length;
            
            return {
              totalProcessed: newResults.length,
              validCount,
              invalidCount,
              errorCount,
              results: newResults,
              processingTime: Date.now() - startTime
            };
          });
        }
      } else if (verificationType === 'peppol') {
        const participantIds: string[] = dataToProcess.map((row) => 
          row.participantId || row.peppolId || row.id || row.identifier || ''
        ).filter(id => id.trim() !== '');

        // Initialize results with empty array
        const initialResult: BatchVerificationResult = {
          totalProcessed: 0,
          validCount: 0,
          invalidCount: 0,
          errorCount: 0,
          results: [],
          processingTime: 0
        };
        setResults(initialResult);

        // Process each participant ID individually and update results in real-time
        for (let i = 0; i < participantIds.length; i++) {
          const userInput = participantIds[i];
          const result = await PeppolVerificationService.verifyPeppolId(userInput, language);
          
          // Check if result contains service unavailable error
          if (result.error) {
            // Show service unavailable modal and stop processing
            setServiceUnavailableModal({
              isOpen: true,
              serviceName: 'Peppol Verification'
            });
            break; // Stop processing remaining items
          }
          
          // Add user input to the result
          const resultWithUserInput = {
            ...result,
            userInput: userInput
          };
          
          // Update progress
          setProgress({ current: i + 1, total: participantIds.length });
          
          // Update results with the new result
          setResults(prev => {
            if (!prev || !('results' in prev)) return initialResult;
            const newResults = [...prev.results, resultWithUserInput];
            const validCount = newResults.filter(r => r.isValid).length;
            const invalidCount = newResults.filter(r => !r.isValid && !r.error).length;
            const errorCount = newResults.filter(r => r.error).length;
            
            return {
              totalProcessed: newResults.length,
              validCount,
              invalidCount,
              errorCount,
              results: newResults,
              processingTime: Date.now() - startTime
            };
          });
        }
      } else if (verificationType === 'both') {
        const identifiers: Array<{ identifier: string; companyName?: string }> = dataToProcess.map((row, index) => ({
          identifier: row.identifier || row.id || row.vatNumber || row.vat || row.taxId || row.participantId || row.peppolId || '',
          companyName: ''
        }));

        // Initialize dual results with empty arrays
        const initialDualResult: DualBatchResult = {
          totalProcessed: 0,
          vatResults: [],
          peppolResults: [],
          processingTime: 0
        };
        setResults(initialDualResult);

        // Process each identifier individually and update results in real-time
        for (let i = 0; i < identifiers.length; i++) {
          const dualResult = await DualVerificationService.verifyBoth(identifiers[i].identifier, identifiers[i].companyName, language);
          
          // Check if either VAT or Peppol result contains service unavailable error
          const vatError = dualResult.vatResult?.error;
          const peppolError = dualResult.peppolResult?.error;
          
          if (vatError || peppolError) {
            // Show service unavailable modal and stop processing
            setServiceUnavailableModal({
              isOpen: true,
              serviceName: 'Dual Verification'
            });
            break; // Stop processing remaining items
          }
          
          // Update progress
          setProgress({ current: i + 1, total: identifiers.length });
          
          // Update results with the new dual result
          setResults(prev => {
            if (!prev || !('vatResults' in prev)) return initialDualResult;
            const newVatResults = [...prev.vatResults, dualResult.vatResult!];
            const newPeppolResults = [...prev.peppolResults, dualResult.peppolResult!];
            
            return {
              totalProcessed: newVatResults.length,
              vatResults: newVatResults,
              peppolResults: newPeppolResults,
              processingTime: Date.now() - startTime
            } as DualBatchResult;
          });
        }
      }
    } catch (error) {
      console.error('Batch verification error:', error);
      // Check if error indicates service unavailable (for unexpected thrown errors)
      const serviceName = verificationType === 'both' ? 'Dual Verification' : 
                          verificationType === 'vat' ? 'VAT Verification' : 'Peppol Verification';
      
      setServiceUnavailableModal({
        isOpen: true,
        serviceName: serviceName
      });
    } finally {
      setIsProcessing(false);
      setProgress(null);
    }
  };

  const handleServiceUnavailableRetry = () => {
    setServiceUnavailableModal({ isOpen: false, serviceName: '' });
    // Retry the batch verification
    processBatchVerification();
  };

  const handleServiceUnavailableClose = () => {
    setServiceUnavailableModal({ isOpen: false, serviceName: '' });
  };

  const exportResults = () => {
    if (!results) return;

    // Handle dual batch results
    if ('vatResults' in results && 'peppolResults' in results) {
      const dualResults = results as DualBatchResult;
      const exportData = dualResults.vatResults.map((vatResult, index) => {
        const peppolResult = dualResults.peppolResults[index];
        return {
          identifier: vatResult.vatNumber,
          vatValid: vatResult.isValid,
          vatCompanyName: vatResult.companyName || '',
          vatAddress: (vatResult.address || '').replace(/[\r\n]+/g, ' '),
          vatCountry: vatResult.country || '',
          vatError: vatResult.error || '',
          peppolValid: peppolResult.isValid,
          peppolFullParticipantId: peppolResult.fullParticipantId || '',
          peppolParticipantName: peppolResult.participantName || '',
          peppolParticipantId: peppolResult.participantId,
          peppolScheme: peppolResult.scheme || '',
          peppolAllParticipantIds: peppolResult.allParticipantIds || '',
          peppolAllEntityNames: peppolResult.allEntityNames || '',
          peppolMatchCount: peppolResult.matchCount || 1,
          peppolAllSupportedDocumentTypes: peppolResult.allSupportedDocumentTypes || '',
          peppolSupportsBilling: peppolResult.supportsBilling || false,
          peppolError: peppolResult.error || '',
          timestamp: vatResult.timestamp
        };
      });

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dual_verification_results_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return;
    }

    const batchResults = results as BatchVerificationResult;
    const exportData = batchResults.results.map(result => {
      if ('vatNumber' in result) {
        return {
          vatNumber: result.vatNumber,
          isValid: result.isValid,
          companyName: result.companyName || '',
          address: (result.address || '').replace(/[\r\n]+/g, ' '),
          country: result.country || '',
          error: result.error || '',
          timestamp: result.timestamp
        };
      } else {
        return {
          fullParticipantId: result.fullParticipantId || '',
          participantName: result.participantName || '',
          participantId: result.participantId,
          scheme: result.scheme || '',
          allParticipantIds: result.allParticipantIds || '',
          allEntityNames: result.allEntityNames || '',
          matchCount: result.matchCount || 1,
          allSupportedDocumentTypes: result.allSupportedDocumentTypes || '',
          supportsBilling: result.supportsBilling || false,
          isValid: result.isValid,
          error: result.error || '',
          timestamp: result.timestamp
        };
      }
    });

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verification_results_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    let templateData: any[] = [];
    
    if (verificationType === 'vat') {
      templateData = [
        {
          vatNumber: 'DE123456789'
        }
      ];
    } else if (verificationType === 'peppol') {
      templateData = [
        {
          participantId: '123456789'
        }
      ];
    } else {
      templateData = [
        {
          identifier: 'DE123456789'
        }
      ];
    }

    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${verificationType}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const renderResults = () => {
    if (!results) return null;

    // Handle dual batch results
    if ('vatResults' in results && 'peppolResults' in results) {
      return renderDualResults(results as DualBatchResult);
    }

    const batchResults = results as BatchVerificationResult;

    return (
      <div className="card mt-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Verification Results</h3>
          <button
            onClick={exportResults}
            className="btn-primary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
{t.batchVerification.forms.exportResults}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{batchResults.totalProcessed}</div>
            <div className="text-sm text-blue-600">{t.batchVerification.results.totalProcessed}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {verificationType === 'peppol' ? (
                (() => {
                  const validResults = batchResults.results.filter(r => r.isValid);
                  const supportsBillingCount = validResults.filter(r => (r as any).supportsBilling).length;
                  return `${supportsBillingCount} / ${validResults.length}`;
                })()
              ) : (
                batchResults.validCount
              )}
            </div>
            <div className="text-sm text-green-600">
              {verificationType === 'peppol' ? 'Supports Billing / Valid' : 'Valid'}
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{batchResults.invalidCount}</div>
            <div className="text-sm text-red-600">Invalid</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{batchResults.errorCount}</div>
            <div className="text-sm text-yellow-600">Errors</div>
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          {t.batchVerification.results.processingTime}: {(batchResults.processingTime / 1000).toFixed(2)} seconds
        </div>

        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {verificationType === 'vat' ? (
                  <>
                    <th className="px-4 py-2 text-left">VAT Number</th>
                    <th className="px-4 py-2 text-left">Company Name</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Error</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-2 text-left">Identifier</th>
                    <th className="px-4 py-2 text-left">Full Participant ID</th>
                    <th className="px-4 py-2 text-left">Participant Name</th>
                    <th className="px-4 py-2 text-left">Supports Billing</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Error</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {results.results.map((result, index) => {
                const isVatResult = 'vatNumber' in result;
                const isValid = result.isValid;
                const StatusIcon = isValid ? CheckCircle : (result.error ? AlertCircle : XCircle);
                const statusColor = isValid ? 'text-green-600' : (result.error ? 'text-yellow-600' : 'text-red-600');

                return (
                  <tr key={index} className="border-b border-gray-200">
                    {isVatResult ? (
                      <>
                        <td className="px-4 py-2">{result.vatNumber}</td>
                        <td className="px-4 py-2">{result.companyName || '-'}</td>
                        <td className="px-4 py-2">
                          <div className={`flex items-center gap-2 ${statusColor}`}>
                            <StatusIcon className="w-4 h-4" />
                            {isValid ? 'Valid' : (result.error ? 'Error' : 'Invalid')}
                          </div>
                        </td>
                        <td className="px-4 py-2">{result.error || '-'}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-2">{(result as any).userInput || result.participantId}</td>
                        <td className="px-4 py-2">
                          <div className="text-sm font-mono whitespace-pre-line">
                            {result.allParticipantIds ? result.allParticipantIds.replace(/\s*\|\s*/g, '\n') : (result.fullParticipantId || '-')}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="whitespace-pre-line">
                            {result.allEntityNames ? result.allEntityNames.replace(/\s*\|\s*/g, '\n') : (result.participantName || '-')}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className={`flex items-center gap-2 ${result.supportsBilling ? 'text-green-600' : 'text-red-600'}`}>
                            {result.supportsBilling ? (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                Yes
                              </>
                            ) : (
                              <>
                                <XCircle className="w-4 h-4" />
                                No
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className={`flex items-center gap-2 ${statusColor}`}>
                            <StatusIcon className="w-4 h-4" />
                            {isValid ? 'Valid' : (result.error ? 'Error' : 'Invalid')}
                          </div>
                        </td>
                        <td className="px-4 py-2">{result.error || '-'}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDualResults = (dualResults: DualBatchResult) => {
    const vatValidCount = dualResults.vatResults.filter(r => r.isValid).length;
    const vatInvalidCount = dualResults.vatResults.filter(r => !r.isValid && !r.error).length;
    const vatErrorCount = dualResults.vatResults.filter(r => r.error).length;
    
    const peppolInvalidCount = dualResults.peppolResults.filter(r => !r.isValid && !r.error).length;
    const peppolErrorCount = dualResults.peppolResults.filter(r => r.error).length;

    return (
      <div className="card mt-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">{t.batchVerification.results.dualVerificationResults}</h3>
          <button
            onClick={exportResults}
            className="btn-primary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
{t.batchVerification.forms.exportResults}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* VAT Results Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-800 mb-4">{t.batchVerification.results.vatVerification}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded">
                <div className="text-xl font-bold text-blue-600">{vatValidCount}</div>
                <div className="text-sm text-blue-600">Valid</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="text-xl font-bold text-red-600">{vatInvalidCount}</div>
                <div className="text-sm text-red-600">Invalid</div>
              </div>
            </div>
            {vatErrorCount > 0 && (
              <div className="mt-2 text-sm text-yellow-600">
                {vatErrorCount} errors
              </div>
            )}
          </div>

          {/* Peppol Results Summary */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-green-800 mb-4">{t.singleVerification.types.peppol}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded">
                <div className="text-xl font-bold text-green-600">
                  {(() => {
                    const validPeppolResults = dualResults.peppolResults.filter(r => r.isValid);
                    const supportsBillingCount = validPeppolResults.filter(r => r.supportsBilling).length;
                    return `${supportsBillingCount} / ${validPeppolResults.length}`;
                  })()}
                </div>
                <div className="text-sm text-green-600">Supports Billing / Valid</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="text-xl font-bold text-red-600">{peppolInvalidCount}</div>
                <div className="text-sm text-red-600">Invalid</div>
              </div>
            </div>
            {peppolErrorCount > 0 && (
              <div className="mt-2 text-sm text-yellow-600">
                {peppolErrorCount} errors
              </div>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          {t.batchVerification.results.processingTime}: {(dualResults.processingTime / 1000).toFixed(2)} seconds
        </div>

        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">Identifier</th>
                <th className="px-4 py-2 text-left">VAT Status</th>
                <th className="px-4 py-2 text-left">VAT Company Name</th>
                <th className="px-4 py-2 text-left">Peppol Status</th>
                <th className="px-4 py-2 text-left">Peppol Company Name</th>
                <th className="px-4 py-2 text-left">Peppol Full Participant ID</th>
                <th className="px-4 py-2 text-left">Supports Billing</th>
                <th className="px-4 py-2 text-left">VAT Error</th>
                <th className="px-4 py-2 text-left">Peppol Error</th>
              </tr>
            </thead>
            <tbody>
              {dualResults.vatResults.map((vatResult, index) => {
                const peppolResult = dualResults.peppolResults[index];
                const VatStatusIcon = vatResult.isValid ? CheckCircle : (vatResult.error ? AlertCircle : XCircle);
                const vatStatusColor = vatResult.isValid ? 'text-green-600' : (vatResult.error ? 'text-yellow-600' : 'text-red-600');
                const PeppolStatusIcon = peppolResult.isValid ? CheckCircle : (peppolResult.error ? AlertCircle : XCircle);
                const peppolStatusColor = peppolResult.isValid ? 'text-green-600' : (peppolResult.error ? 'text-yellow-600' : 'text-red-600');

                return (
                  <tr key={index} className="border-b border-gray-200">
                    <td className="px-4 py-2 font-medium">{vatResult.vatNumber}</td>
                    <td className="px-4 py-2">
                      <div className={`flex items-center gap-2 ${vatStatusColor}`}>
                        <VatStatusIcon className="w-4 h-4" />
                        {vatResult.isValid ? 'Valid' : (vatResult.error ? 'Error' : 'Invalid')}
                      </div>
                    </td>
                    <td className="px-4 py-2">{vatResult.companyName || '-'}</td>
                    <td className="px-4 py-2">
                      <div className={`flex items-center gap-2 ${peppolStatusColor}`}>
                        <PeppolStatusIcon className="w-4 h-4" />
                        {peppolResult.isValid ? 'Valid' : (peppolResult.error ? 'Error' : 'Invalid')}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="whitespace-pre-line">
                        {peppolResult.allEntityNames ? peppolResult.allEntityNames.replace(/\s*\|\s*/g, '\n') : (peppolResult.participantName || '-')}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm font-mono whitespace-pre-line">
                        {peppolResult.allParticipantIds ? peppolResult.allParticipantIds.replace(/\s*\|\s*/g, '\n') : (peppolResult.fullParticipantId || '-')}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className={`flex items-center gap-2 ${peppolResult.supportsBilling ? 'text-green-600' : 'text-red-600'}`}>
                        {peppolResult.supportsBilling ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Yes
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4" />
                            No
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">{vatResult.error || '-'}</td>
                    <td className="px-4 py-2">{peppolResult.error || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Upload className="w-6 h-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">{t.batchVerification.title}</h2>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t.singleVerification.apiMode.simulationMode}</span>
          <button
            onClick={() => setUseRealAPI(!useRealAPI)}
            className="flex items-center gap-2 text-sm font-medium"
          >
            {useRealAPI ? (
              <>
                <ToggleRight className="w-5 h-5 text-primary-600" />
                <span className="text-primary-600">{t.singleVerification.apiMode.realAPI}</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-5 h-5 text-gray-400" />
                <span className="text-gray-500">{t.singleVerification.apiMode.simulation}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t.singleVerification.verificationType}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="vat"
              checked={verificationType === 'vat'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t.singleVerification.types.vat}
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="peppol"
              checked={verificationType === 'peppol'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t.singleVerification.types.peppol}
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="both"
              checked={verificationType === 'both'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {t.singleVerification.types.both}
            </span>
          </label>
        </div>
      </div>

      <div className="mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t.batchVerification.inputMethod}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={inputMethod === 'csv'}
                onChange={(e) => setInputMethod(e.target.value as 'csv' | 'textarea')}
                className="mr-2"
              />
              <span className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {t.batchVerification.methods.csv}
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="textarea"
                checked={inputMethod === 'textarea'}
                onChange={(e) => setInputMethod(e.target.value as 'csv' | 'textarea')}
                className="mr-2"
              />
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {t.batchVerification.methods.textarea}
              </span>
            </label>
          </div>
        </div>

        {inputMethod === 'csv' ? (
          <div>
            <div className="flex gap-3 mb-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn-primary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {t.batchVerification.forms.uploadFile}
              </button>
              <button
                onClick={downloadTemplate}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t.batchVerification.forms.downloadTemplate}
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />

            {uploadedData.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800">
                  <strong>{uploadedData.length}</strong> records loaded from CSV file
                </p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.batchVerification.forms.textAreaPlaceholder}
              </label>
              <textarea
                value={textAreaInput}
                onChange={(e) => {
                  setTextAreaInput(e.target.value);
                  if (e.target.value.trim()) {
                    const identifiers = parseTextAreaInput();
                    Analytics.batchVerificationTextInput(verificationType === 'both' ? 'dual' : verificationType, identifiers.length);
                  }
                }}
                className="input-field"
                rows={8}
                placeholder={t.batchVerification.forms.textInputPlaceholder}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t.batchVerification.forms.textInputHelp}
              </p>
            </div>

            {textAreaInput.trim() && (
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800">
                  <strong>{parseTextAreaInput().length}</strong> {t.batchVerification.forms.identifiersReady}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">
          {inputMethod === 'csv' ? t.batchVerification.format.csvRequirements : t.batchVerification.format.textInputFormat}
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg text-sm">
          {inputMethod === 'csv' ? (
            verificationType === 'vat' ? (
              <div>
                <p className="font-medium mb-2">{t.batchVerification.format.vatVerificationTitle}</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {t.batchVerification.format.vatVerificationColumns.map((column, index) => (
                    <li key={index}>{column}</li>
                  ))}
                </ul>
              </div>
            ) : verificationType === 'peppol' ? (
              <div>
                <p className="font-medium mb-2">{t.batchVerification.format.peppolVerificationTitle}</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {t.batchVerification.format.peppolVerificationColumns.map((column, index) => (
                    <li key={index}>{column}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div>
                <p className="font-medium mb-2">{t.batchVerification.format.dualVerificationTitle}</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {t.batchVerification.format.dualVerificationColumns.map((column, index) => (
                    <li key={index}>{column}</li>
                  ))}
                </ul>
                <p className="text-sm text-gray-500 mt-2">
                  {t.batchVerification.forms.dualVerificationHelp}
                </p>
              </div>
            )
          ) : (
            <div>
              <p className="font-medium mb-2">{t.batchVerification.forms.textInputTitle}</p>
              <p className="text-sm text-gray-600 mb-3">{t.batchVerification.forms.textInputDescription}</p>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                {t.batchVerification.forms.textInputRules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
              <div className="mt-3 p-3 bg-white rounded border">
                <p className="text-xs font-medium text-gray-700 mb-1">Example:</p>
                <pre className="text-xs text-gray-600">
{`DE123456789, FR12345678901
123456789, 987654321
1234567890123`}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={processBatchVerification}
          disabled={isProcessing || (inputMethod === 'csv' ? uploadedData.length === 0 : !textAreaInput.trim())}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.batchVerification.buttons.processing}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              {t.batchVerification.buttons.process}
            </>
          )}
        </button>
        
        {progress && (
          <div className="mt-4 bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">
                {t.batchVerification.progress.processing}: {progress.current} of {progress.total}
              </span>
              <span className="text-sm text-blue-600 ml-4">
                {Math.round((progress.current / progress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {renderResults()}

      {/* Service Unavailable Modal */}
      <ServiceUnavailableModal
        isOpen={serviceUnavailableModal.isOpen}
        onClose={handleServiceUnavailableClose}
        onRetry={handleServiceUnavailableRetry}
        serviceName={serviceUnavailableModal.serviceName}
        retryInSeconds={300} // 5 minutes
      />
    </div>
  );
};

export default BatchVerification;
